import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import { Formik, useFormik } from "formik";
import Loader from "../../../components/spinner/Spinner";
import Backdrop from "../../../components/backdrop/Backdrop";
import Toast from "../../../components/toast/Toast";
import KidsService from "../../../services/kids";
import { uploadImg, uploadImgMaxSize } from "../../../utils/uploadImg";
import DocumentCard from "./DocumentCard";
import { Close } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { DOCUMENT_CATEGORY_OPTIONS } from "../../../utils/documentCategory";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  height: "80vh",
  p: 4,
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  // change background colour if dragging
  background: isDragging ? "lightgreen" : "transparent",
  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function DocumentsModal({ open, onClose, selectedRow }) {
  const [rowData, setRowData] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastStatus, setToastStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState();
  const [backdrop, setBackdrop] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      enrollment_id: "",
      document_url: "",
      document_category: "report",
    },
    onSubmit: async (values) => {
      try {
        if (file) {
          setBackdrop(true);
          const { url, resource_type } = await uploadImg(
            file,
            "documents",
            values.title,
          );
          values.document_url = url;
          values.resource_type = resource_type;
          values.enrollment_id = selectedRow.enrollment_id;

          // Check for duplicate documents with same title
          const isDuplicate = rowData.some(
            (doc) => doc.title === values.title && doc.id !== values.id,
          );

          if (isDuplicate) {
            setBackdrop(false);
            setToastOpen(true);
            setToastStatus("error");
            setToastMsg("A document with this name already exists");
            setFile("");
            document.getElementById("documentUrl").value = "";
            formik.resetForm();
            return;
          }

          if (values.id && url) {
            await KidsService.updateKidDocument(values);
          } else if (url) {
            await KidsService.addKidDocument(values);
          }
          setToastOpen(true);
          setToastMsg(
            `Kid Document ${values?.id ? "updated" : "added"} successfully`,
          );
          setToastStatus("success");
          getAllKidsDocuments();
          setBackdrop(false);
          setFile("");
          document.getElementById("documentUrl").value = "";
          formik.resetForm();
        }
      } catch (err) {
        console.log(err);
        setBackdrop(false);
        setToastOpen(true);
        setToastStatus("error");
        setToastMsg("Something went wrong!");
      }
      console.log(values);
    },
  });

  useEffect(() => {
    getAllKidsDocuments();
  }, []);

  const getAllKidsDocuments = async () => {
    setLoading(true);
    try {
      let { data } = await KidsService.fetchKidsDocuments(
        selectedRow.enrollment_id,
      );
      setRowData(data.data);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const onChangeDocument = (e) => {
    let fileMaxSize = uploadImgMaxSize(e.target.files[0], 1024);
    console.log(fileMaxSize);
    if (!fileMaxSize) {
      setToastOpen(true);
      setToastStatus("error");
      setToastMsg("Max file size " + 1 + "MB allowed.");
      setFile("");
      document.getElementById("documentUrl").value = "";
    } else {
      setFile(e.target.files[0]);
    }
  };

  const onImgDelete = async (row) => {
    if (!window.confirm("Are you sure to delete the record?")) return;
    let { id } = row;
    setBackdrop(true);
    try {
      const { status } = await KidsService.deleteKidDocument(id);
      if (status) {
        setBackdrop(false);
        getAllKidsDocuments();
        setToastMsg("Kid document deleted successfully");
        setToastStatus("success");
      }
    } catch (err) {
      setBackdrop(false);
      setToastMsg("Something went wrong!");
      setToastStatus("error");
    }
  };

  const onDragEnd = async (result) => {
    console.log("=== onDragEnd START ===");
    console.log("Drag result:", result);

    if (!result.destination) {
      console.log("No destination, returning early");
      return;
    }

    console.log("Current rowData:", rowData);
    console.log("Source index:", result.source.index);
    console.log("Destination index:", result.destination.index);

    const items = Array.from(rowData);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    console.log("Reordered items:", items);

    // Update local state with new order
    setRowData(items);

    // Prepare data for API call - assign display_order based on new index
    const documentsWithOrder = items.map((doc, index) => ({
      id: doc.id,
      display_order: index,
    }));

    console.log("Sending to API:", documentsWithOrder);

    try {
      setBackdrop(true);
      console.log("=== Calling KidsService.reorderKidDocuments ===");
      const response =
        await KidsService.reorderKidDocuments(documentsWithOrder);
      console.log("=== API Response Received ===");
      console.log("Full response object:", response);
      console.log("Response status:", response?.status);
      console.log("Response status text:", response?.statusText);
      console.log("Response data:", response?.data);
      console.log("Response headers:", response?.headers);
      setBackdrop(false);
      setToastOpen(true);
      setToastMsg("Documents order updated successfully");
      setToastStatus("success");
    } catch (err) {
      console.error("=== ERROR in onDragEnd ===");
      console.error("Full error object:", err);
      console.error("Error message:", err?.message);
      console.error("Error code:", err?.code);
      console.error("Error config:", err?.config);
      console.error("Error response:", err?.response);
      console.error("Error response status:", err?.response?.status);
      console.error("Error response status text:", err?.response?.statusText);
      console.error("Error response data:", err?.response?.data);
      console.error("Error response headers:", err?.response?.headers);
      setBackdrop(false);
      setToastOpen(true);
      setToastStatus("error");
      setToastMsg("Failed to update documents order");
    }
    console.log("=== onDragEnd END ===");
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="modal-lg">
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {selectedRow.name} Documents
            <Close
              onClick={onClose}
              sx={{ mt: 1, float: "right", cursor: "pointer" }}
              variant="outlined"
            >
              Close
            </Close>
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" margin="dense">
                  <InputLabel id="document-category-label">Category</InputLabel>
                  <Select
                    labelId="document-category-label"
                    name="document_category"
                    label="Category"
                    value={formik.values.document_category}
                    onChange={formik.handleChange}
                  >
                    {DOCUMENT_CATEGORY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <TextField
                    name="title"
                    label="Enter Document Name"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    margin="dense"
                    size="small"
                    fullWidth
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  type="file"
                  name="document_url"
                  margin="dense"
                  size="small"
                  id="documentUrl"
                  accept="application/pdf, image/*"
                  onChange={onChangeDocument}
                />
                {file && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, display: "block", color: "#666" }}
                  >
                    Selected: {file.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={2}>
                <Button
                  color="primary"
                  sx={{ mr: 1, mt: 1 }}
                  variant="contained"
                  onClick={formik.handleSubmit}
                >
                  Upload
                </Button>
              </Grid>
            </Grid>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="documents" direction="horizontal">
                {(provided) => (
                  <Grid
                    container
                    spacing={2}
                    sx={{ mt: 2 }}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {loading ? (
                      <Loader />
                    ) : (
                      <>
                        {rowData?.length > 0 &&
                          rowData.map((data, index) => (
                            <Draggable
                              key={data.id}
                              draggableId={String(data.id)}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Grid
                                  item
                                  xs={3}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style,
                                  )}
                                >
                                  <DocumentCard
                                    onImgDelete={onImgDelete}
                                    data={data}
                                  />
                                </Grid>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </>
                    )}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          </Typography>
        </Box>
      </Modal>
      {toastOpen && (
        <Toast
          toastOpen={toastOpen}
          toastClose={() => setToastOpen(false)}
          toastMsg={toastMsg}
          toastStatus={toastStatus}
        />
      )}
      {backdrop && <Backdrop />}
    </div>
  );
}
